/**
 * Frontend analytics helper — envoie les événements au backend (best-effort, jamais bloquant).
 *
 * Usage :
 *   import { trackEvent } from '@/lib/analytics';
 *   trackEvent('proposition_card_viewed', { reference: 'SIM-XXXX', source: 'admin_table' });
 */

const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API}/api/analytics/events`;

const KNOWN_EVENTS = new Set([
  'proposition_card_viewed',
  'proposition_card_downloaded',
  'proposition_card_souscrire_clicked',
  'document_lightbox_opened',
  'document_downloaded',
  'profile_completeness_complete_clicked',
]);

// Évite le tracking multiple du MÊME event sur le MÊME identifiant dans la session courante (déduplication).
const seen = new Set();

/**
 * Envoie un événement analytics au backend.
 * @param {string} event - Nom de l'événement (whitelist côté serveur).
 * @param {object} [properties={}] - Données contextuelles (reference, source, etc.)
 * @param {object} [opts]
 * @param {boolean} [opts.dedupKey] - Si fourni, déduplique par event + key dans la session courante.
 */
export function trackEvent(event, properties = {}, opts = {}) {
  if (!KNOWN_EVENTS.has(event)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[analytics] Unknown event: ${event}`);
    }
    return;
  }

  if (opts.dedupKey) {
    const key = `${event}:${opts.dedupKey}`;
    if (seen.has(key)) return;
    seen.add(key);
  }

  // Get auth token if available (lazy import - localStorage)
  let authHeader = {};
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('access_token');
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  } catch {
    // ignore
  }

  // Best effort fire-and-forget. Use sendBeacon if available for reliability on unload.
  const body = JSON.stringify({ event, properties });

  if (typeof navigator !== 'undefined' && navigator.sendBeacon && !authHeader.Authorization) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    } catch {
      // fallback to fetch
    }
  }

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body,
      keepalive: true,
    }).catch(() => {
      // silent — never break UX for analytics
    });
  } catch {
    // ignore
  }
}
